import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Step, DrivingStep, CustomStep } from './types';
import AddressField from './AddressField';
import { fetchDrivingTime, hasMapsKey } from './google';

const DEFAULT_FROM_ADDRESS = '358 Red Osier Road, Waterloo, ON';

const useStyles = makeStyles(theme => ({
  card: {
    padding: '0.6em 0.6em 0.5em',
    marginBottom: '0.6em',
    textAlign: 'left',
    [theme.breakpoints.up('sm')]: {
      padding: '0.75em 1em',
    },
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35em',
    marginBottom: '0.5em',
  },
  name: {
    flex: 1,
    minWidth: 0,
  },
  reorderCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  fieldRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5em',
    alignItems: 'center',
    marginBottom: '0.5em',
  },
  addrField: {
    flex: '1 1 100%',
    minWidth: 0,
    [theme.breakpoints.up('sm')]: {
      flex: '1 1 220px',
    },
  },
  paddingRow: {
    display: 'flex',
    gap: '0.4em',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  paddingField: {
    width: 88,
  },
  unitField: {
    width: 110,
  },
  drivingMinField: {
    width: 110,
  },
  drivingResult: {
    fontSize: '0.8em',
    color: '#444',
  },
  paddingHint: {
    color: '#777',
    fontSize: '0.8em',
    whiteSpace: 'nowrap',
  },
  drivingError: {
    fontSize: '0.8em',
    color: '#b00020',
  },
}));

interface Props {
  step: Step;
  index: number;
  total: number;
  focusName?: boolean;
  onChange: (id: string, step: Step) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
}

function StepCard({
  step, index, total, focusName = false,
  onChange, onDelete, onMove,
}: Props) {
  const classes = useStyles();
  const [lookingUp, setLookingUp] = useState(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!focusName || !nameInputRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusName, step.id]);

  const updateName = (name: string) => onChange(step.id, { ...step, name });
  const updatePaddingValue = (v: string) => {
    if (step.type !== 'driving') return;
    onChange(step.id, { ...step, paddingValue: v === '' ? null : (parseFloat(v) || 0) } as DrivingStep);
  };
  const updatePaddingUnit = (unit: string) => {
    if (step.type !== 'driving') return;
    onChange(step.id, { ...step, paddingUnit: unit === 'percent' ? 'percent' : 'minutes' } as DrivingStep);
  };
  const paddingMinutes = step.type === 'driving' && step.paddingUnit === 'percent' && step.drivingMinutes !== null
    ? Math.round(Math.max(0, step.drivingMinutes) * Math.max(0, step.paddingValue || 0) / 100)
    : null;

  const lookupDriving = async () => {
    if (step.type !== 'driving') return;
    if (!step.origin || !step.destination) return;
    setLookingUp(true);
    try {
      const result = await fetchDrivingTime(step.origin, step.destination);
      const updated: DrivingStep = {
        ...step,
        drivingMinutes: result.minutes,
        lastLookupAt: Date.now(),
      };
      delete updated.drivingError;
      onChange(step.id, updated);
    } catch (err: any) {
      onChange(step.id, { ...step, drivingError: err && err.message ? err.message : 'Lookup failed' } as DrivingStep);
    } finally {
      setLookingUp(false);
    }
  };

  return (
    <Paper className={classes.card} elevation={2}>
      <div className={classes.topRow}>
        <div className={classes.reorderCol}>
          <IconButton size="small" disabled={index === 0} onClick={() => onMove(step.id, -1)} aria-label="move earlier">
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(step.id, 1)} aria-label="move later">
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </div>
        <TextField
          className={classes.name}
          size="small"
          variant="outlined"
          label="Step name"
          value={step.name}
          inputRef={nameInputRef}
          onChange={e => updateName(e.target.value)}
        />
        <IconButton size="small" onClick={() => onDelete(step.id)} aria-label="delete step">
          <DeleteIcon />
        </IconButton>
      </div>

      {step.type === 'custom' && (
        <div className={classes.fieldRow}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Duration (min)"
            type="number"
            value={(step as CustomStep).durationMinutes ?? ''}
            onChange={e => onChange(step.id, {
              ...step,
              durationMinutes: e.target.value === '' ? null : (parseFloat(e.target.value) || 0),
            } as CustomStep)}
            inputProps={{ min: 0, inputMode: 'numeric' }}
          />
        </div>
      )}

      {step.type === 'driving' && (
        <>
          <div className={classes.fieldRow}>
            <div className={classes.addrField}>
              <AddressField
                label="From"
                value={(step as DrivingStep).origin}
                displayValue="Home"
                displayMatchesValue={(step as DrivingStep).origin === DEFAULT_FROM_ADDRESS}
                onChange={v => onChange(step.id, { ...step, origin: v } as DrivingStep)}
              />
            </div>
            <div className={classes.addrField}>
              <AddressField
                label="To"
                value={(step as DrivingStep).destination}
                onChange={v => onChange(step.id, { ...step, destination: v } as DrivingStep)}
              />
            </div>
          </div>
          <div className={classes.fieldRow}>
            <Tooltip title={hasMapsKey() ? 'Look up driving time' : 'Set REACT_APP_GOOGLE_MAPS_API_KEY to enable'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  disabled={lookingUp || !hasMapsKey() || !(step as DrivingStep).origin || !(step as DrivingStep).destination}
                  onClick={lookupDriving}
                >
                  {lookingUp ? 'Looking up…' : 'Get driving time'}
                </Button>
              </span>
            </Tooltip>
            <TextField
              className={classes.drivingMinField}
              size="small"
              variant="outlined"
              label="Min"
              type="number"
              value={(step as DrivingStep).drivingMinutes ?? ''}
              onChange={e => onChange(step.id, { ...step, drivingMinutes: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) } as DrivingStep)}
              inputProps={{ min: 0, inputMode: 'numeric' }}
            />
            {(step as DrivingStep).drivingError ? (
              <span className={classes.drivingError}>{(step as DrivingStep).drivingError}</span>
            ) : (step as DrivingStep).lastLookupAt ? (
              <span className={classes.drivingResult}>
                Updated {new Date((step as DrivingStep).lastLookupAt as number).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            ) : null}
          </div>
        </>
      )}

      {step.type === 'driving' && (
        <div className={classes.paddingRow}>
          <Typography variant="body2">Pad</Typography>
          <TextField
            className={classes.paddingField}
            size="small"
            variant="outlined"
            type="number"
            value={step.paddingValue ?? ''}
            onChange={e => updatePaddingValue(e.target.value)}
            inputProps={{ min: 0, inputMode: 'numeric' }}
          />
          <TextField
            className={classes.unitField}
            size="small"
            variant="outlined"
            select
            value={step.paddingUnit}
            onChange={e => updatePaddingUnit(e.target.value)}
          >
            <MenuItem value="minutes">minutes</MenuItem>
            <MenuItem value="percent">percent</MenuItem>
          </TextField>
          {paddingMinutes !== null && (
            <Typography className={classes.paddingHint} variant="body2">
              {paddingMinutes} min
            </Typography>
          )}
        </div>
      )}
    </Paper>
  );
}

export default React.memo(StepCard);
